import { HttpTransportType, HubConnectionBuilder, LogLevel, type HubConnection } from "@microsoft/signalr";
import { REALTIME_ORIGIN, api, setConnectionId } from "@shared/api";
import type { RealtimeEvent } from "@shared/api";
import { receiveRealtime, telemetryEvent } from "@shared/telemetry";

export type ConnectionState = "connecting" | "live" | "lost";

type Listener = (event: RealtimeEvent) => void;
type StateListener = (state: ConnectionState, attempt: number) => void;

/**
 * Одно соединение на всё приложение.
 *
 * Метод у хаба один — `Realtime`; событие разбирается по паре context+action.
 * Токен подключения одноразовый и короткий, поэтому берётся заново на каждое
 * соединение, включая переподключения. По той же причине переподключение
 * своё, а не `withAutomaticReconnect`: тот пришёл бы с уже потраченным токеном.
 */
class Realtime {
  private connection: HubConnection | null = null;
  /** Идёт подключение: нужен, потому что между запросом токена и появлением
   *  соединения есть окно, и без него второй вызов заводит второе соединение. */
  private opening = false;
  private wanted = false;
  private timer: number | null = null;

  private listeners = new Set<Listener>();
  private stateListeners = new Set<StateListener>();
  private state: ConnectionState = "connecting";
  private attempt = 0;

  on(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onState(listener: StateListener): () => void {
    this.stateListeners.add(listener);
    listener(this.state, this.attempt);
    return () => this.stateListeners.delete(listener);
  }

  private setState(state: ConnectionState) {
    telemetryEvent("realtime.connection", { state, attempt: String(this.attempt) });
    this.state = state;
    for (const listener of this.stateListeners) listener(state, this.attempt);
  }

  async connect() {
    this.wanted = true;
    if (this.connection || this.opening) return;

    this.opening = true;
    this.setState(this.attempt === 0 ? "connecting" : "lost");

    let token: string;
    try {
      token = (await api.auth.connectionToken()).connection_token;
    } catch {
      this.opening = false;
      // Токен не выдали — скорее всего, сессия кончилась. Повтор возьмёт
      // свежий, когда доступ обновится.
      this.retryLater();
      return;
    }

    // Пока ходили за токеном, подключение могли отменить.
    if (!this.wanted) {
      this.opening = false;
      return;
    }

    /*
     * В вебе узел скрыт за прокси и адрес пустой; в собранном приложении он
     * свой — у него отдельный порт, и адрес API сюда не подходит.
     *
     * Только WebSocket и без согласования: у узлов нет общих сессий, и
     * согласование, пришедшее на соседний узел, соединения бы не нашло. Токен
     * браузер может передать только в адресе (`?access_token=`), поэтому
     * журнал SignalR — не ниже предупреждений: на уровне Information он пишет
     * адрес подключения целиком.
     */
    const connection = new HubConnectionBuilder()
      .withUrl(`${REALTIME_ORIGIN}/realtime`, {
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets,
        accessTokenFactory: () => token,
      })
      .configureLogging(LogLevel.Warning)
      .build();
    this.connection = connection;
    this.opening = false;

    // Обработчики смотрят на своё соединение, а не на текущее: устаревшее
    // иначе закрывало бы живое, пришедшее ему на смену.
    connection.on("Realtime", (event: RealtimeEvent) => {
      if (this.connection !== connection) return;

      /*
       * `ready` приходит дважды: сначала от узла — с id соединения, потом от
       * API — со списком каналов, когда он завёл соединение в комнаты. Id
       * нужен API в `x-connection-id`: по нему своя вкладка не получает эхом
       * то, что уже пришло ей в ответе на запрос.
       */
      if (event.context === "connection" && event.action === "ready") {
        const id = (event.payload as { connection_id?: unknown }).connection_id;
        if (typeof id === "string") setConnectionId(id);
      }

      receiveRealtime(event._trace, `${event.context}.${event.action}`, () => {
        for (const listener of this.listeners) listener(event);
      });
    });

    // Закрытие приходит только по уже открытому соединению; несостоявшееся
    // подключение видно по отказу `start`.
    connection.onclose(() => this.dropped(connection));

    connection.start().then(
      () => {
        if (this.connection !== connection) return;
        this.attempt = 0;
        this.setState("live");
      },
      () => this.dropped(connection),
    );
  }

  private dropped(connection: HubConnection) {
    connection.off("Realtime");
    void connection.stop();

    if (this.connection !== connection) return;

    this.connection = null;
    setConnectionId(null);

    if (!this.wanted) return;

    this.attempt += 1;
    this.setState("lost");
    this.retryLater();
  }

  /**
   * Пауза растёт до десяти секунд: сервер, который лёг, не поднимут частые
   * попытки — они только добавят ему нагрузки.
   */
  private retryLater() {
    if (this.timer !== null) return;

    const delay = Math.min(10_000, 1000 * 2 ** Math.min(this.attempt, 4));
    this.timer = window.setTimeout(() => {
      this.timer = null;
      if (this.wanted) void this.connect();
    }, delay);
  }

  disconnect() {
    this.wanted = false;

    if (this.timer !== null) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }

    const connection = this.connection;
    this.connection = null;
    setConnectionId(null);

    if (connection) {
      connection.off("Realtime");
      void connection.stop();
    }

    this.attempt = 0;
    this.setState("connecting");
  }
}

export const realtime = new Realtime();
